import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axios/axiosConfig";
import { User, EnvelopeSimple, Phone, Lock, UploadSimple, Trash, ShieldCheck } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Switch,
  Card, CardHeader, CardTitle, CardBody,
} from "../../components/ui";

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    phone: "",
    image: null,
    role: "",
    double_auth: false,
  });
  const [passwordData, setPasswordData] = useState({ current: "", newPass: "", confirmNewPass: "" });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/users/profile");
      setProfileData(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 15 * 1024 * 1024) {
      const reader = new FileReader();
      reader.onloadend = () => setProfileData((prev) => ({ ...prev, image: reader.result }));
      reader.readAsDataURL(file);
    } else {
      toast.error("Image size should be less than 15MB");
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axiosInstance.put("/users/updateProfile", {
        ...profileData,
        double_auth: profileData.double_auth,
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPass !== passwordData.confirmNewPass) {
      toast.error("New passwords do not match");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.put("/users/changePassword", passwordData);
      toast.success(response.data.message);
      setPasswordData({ current: "", newPass: "", confirmNewPass: "" });
    } catch (error) {
      toast.error("Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl p-6 text-start">
      <PageHeader title="Profile settings" subtitle="Update your personal information and password." />

      <div className="space-y-6">
        {/* Profile */}
        <form id="profile-form" onSubmit={updateProfile}>
          <Card>
            <CardHeader>
              <CardTitle>Profile information</CardTitle>
              <Button type="submit" size="sm" loading={loading}>
                Save profile
              </Button>
            </CardHeader>
            <CardBody className="space-y-6">
              {/* Avatar */}
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-full border border-line bg-surface-2">
                    {profileData.image ? (
                      <img src={profileData.image} alt="Profile" className="h-full w-full object-cover" />
                    ) : (
                      <User size={30} className="text-faint" />
                    )}
                  </div>
                  <label
                    htmlFor="photo-upload"
                    className="absolute bottom-0 right-0 grid h-7 w-7 cursor-pointer place-items-center rounded-full bg-accent text-accent-fg shadow transition hover:bg-accent-strong"
                  >
                    <UploadSimple size={13} />
                    <input id="photo-upload" type="file" className="hidden" onChange={handleImageChange} accept="image/*" name="image" />
                  </label>
                </div>
                <div className="space-y-1.5">
                  {profileData.image && (
                    <button
                      type="button"
                      onClick={() => setProfileData((p) => ({ ...p, image: null }))}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 transition hover:text-red-700"
                    >
                      <Trash size={15} /> Remove photo
                    </button>
                  )}
                  <p className="text-xs text-muted">JPG, PNG or GIF · max 15MB</p>
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <Input label="Full name" icon={User} name="fullName" value={profileData.fullName || ""} onChange={handleProfileChange} placeholder="Your name" />
                <Input label="Email" icon={EnvelopeSimple} type="email" name="email" value={profileData.email || ""} onChange={handleProfileChange} placeholder="you@example.com" />
                <Input label="Phone" icon={Phone} type="tel" name="phone" value={profileData.phone || ""} onChange={handleProfileChange} placeholder="Phone number" />
              </div>

              {/* 2FA */}
              <div className="flex items-center justify-between gap-4 rounded-xl border border-line bg-surface-2 px-4 py-3.5">
                <div className="flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-line bg-surface text-muted">
                    <ShieldCheck size={20} />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-ink">Two-factor authentication</h3>
                    <p className="mt-0.5 text-sm text-muted">Add an extra layer of security to your account.</p>
                  </div>
                </div>
                <Switch
                  checked={!!profileData.double_auth}
                  onChange={(val) => setProfileData((p) => ({ ...p, double_auth: val }))}
                />
              </div>
            </CardBody>
          </Card>
        </form>

        {/* Password */}
        <form id="password-form" onSubmit={changePassword}>
          <Card>
            <CardHeader>
              <CardTitle>Change password</CardTitle>
            </CardHeader>
            <CardBody className="space-y-5">
              <Input label="Current password" icon={Lock} type="password" name="current" value={passwordData.current} onChange={handlePasswordChange} placeholder="••••••••" />
              <div className="grid gap-5 md:grid-cols-2">
                <Input label="New password" icon={Lock} type="password" name="newPass" value={passwordData.newPass} onChange={handlePasswordChange} placeholder="••••••••" />
                <Input label="Confirm new password" icon={Lock} type="password" name="confirmNewPass" value={passwordData.confirmNewPass} onChange={handlePasswordChange} placeholder="••••••••" />
              </div>
              <div className="flex justify-end">
                <Button type="submit" loading={loading}>
                  Change password
                </Button>
              </div>
            </CardBody>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default Settings;
